import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, skip, startWith } from 'rxjs/operators';

import { Addresses } from 'src/providers/customer/addresses';
import { PhotoNotes, PhotoNoteType } from 'src/providers/photo-notes/photo-notes';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import {
  AddressGuidebookService,
  GUIDEBOOK_V2_CATEGORIES,
  GuidebookV2CategoryId,
  GuidebookV2Field,
  PAID_PARKING_FIELD_KEY,
  ReservationSummary,
  UpsertGuidebookEntry,
} from 'src/shared/services/address-guidebook.service';
import { guidebookPhotoType, isRentalAccount } from 'src/shared/services/guidebook-address-fields';
import {
  AddressFieldValue,
  buildCategoryAddressPatch,
  paidParkingDependentKeys,
} from 'src/shared/services/guidebook-address-patch';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';
import {
  buildGuidebookModel,
  createCustomEntry,
  entryUpsert,
  GuidebookAudience,
  GuidebookModel,
  GuidebookSectionEditorParams,
  TOP_LEVEL_FIELDS,
  withAudience,
  withText,
} from 'src/shared/services/guidebook-entries';

import { PhotoNoteRoomModel } from 'src/models/to-do.model';
import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { ParkingOption } from 'src/shared/constants/parking-options';
import { PhotoNoteUrlModel } from 'src/shared/components/photo-caption/photo-caption.component';

import { ReservationPage } from 'src/pages/reservation/reservation';
import { GuidebookDoorCodesComponent } from '../components/guidebook-door-codes/guidebook-door-codes.component';
import { SmartlockTestCodeComponent } from 'src/shared/components/smartlock-test-code/smartlock-test-code.component';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

type ResolvedTarget =
  | { type: 'text'; key: string }
  | { type: 'category'; categoryId: GuidebookV2CategoryId }
  | { type: 'custom'; key: string | null };

type RowForm = FormGroup<{
  key: FormControl<string>;
  label: FormControl<string>;
  value: FormControl<string>;
  deleted: FormControl<boolean>;
}>;

type FieldValue = AddressFieldValue;

const OWN_SECTION = 'own';

/**
 * Secondary-layer editor for one guidebook section. Opens as a right-side panel on
 * desktop and a full page on mobile; the detail page refreshes when it closes.
 */
@Component({
  selector: 'app-guidebook-section-editor',
  templateUrl: './guidebook-section-editor.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
    GuidebookDoorCodesComponent,
  ],
  // Own instance so photo-caption's delete confirmation only reaches this editor.
  providers: [PhotoCaptionService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GuidebookSectionEditorComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly addressGuidebookService = inject(AddressGuidebookService);
  private readonly addresses = inject(Addresses);
  private readonly photoNotes = inject(PhotoNotes);
  private readonly photoCaptionService = inject(PhotoCaptionService);
  readonly windowService = inject(WindowService);

  private readonly isRental = isRentalAccount();
  private addressPhotoNotes: PhotoNoteRoomModel[] = [];
  private paidParkingSubscription: Subscription | null = null;
  /** Photo awaiting delete confirmation; photo-caption only announces that a delete happened. */
  private pendingDeletePhotoId: number | null = null;

  addressId: number | null = null;
  params: GuidebookSectionEditorParams | null = null;
  model: GuidebookModel | null = null;
  isRightSideContent = false;
  isDataLoaded = false;
  isSaving = false;
  submitted = false;
  errorMessage = '';
  pageTitle = 'Edit Section';
  saveLabel = 'Save';
  audience: GuidebookAudience = 'guest';
  target: ResolvedTarget | null = null;
  isAddFlow = false;
  showAddPicker = false;

  textLabel = '';
  categoryLabel = '';
  categoryFields: GuidebookV2Field[] = [];
  /** categoryFields minus the paid-only ones while parking is free. */
  visibleCategoryFields: GuidebookV2Field[] = [];
  isFixedCategory = false;
  fieldControls: Record<string, FormControl<FieldValue>> = {};
  fieldOptions: Record<string, ParkingOption[]> = {};
  photoType: PhotoNoteType | null = null;
  /** How to Get In also shows the door code and the next stays' codes. */
  isAccessCategory = false;
  photoUrls: PhotoNoteUrlModel[] = [];

  addOptions: SelectModel[] = [];
  readonly placementOptions: SelectModel[] = [
    { value: OWN_SECTION, viewValue: 'Its own section' },
    ...GUIDEBOOK_V2_CATEGORIES
      .filter(category => category.id !== 'contacts' && !category.fixed)
      .map(category => ({ value: category.id, viewValue: `Inside ${category.label}` })),
  ];
  readonly audienceOptions: RadioButtonModel[] = [
    { value: 'guest', viewValue: 'Guest Guidebook', extraText: 'Shown to guests on the property website.' },
    { value: 'internal', viewValue: 'Internal Guidebook', extraText: 'Only your team and TIDY see it. Never shown to guests.' },
  ];
  readonly fields = new FormGroup<Record<string, FormControl<FieldValue>>>({});
  readonly rows = new FormArray<RowForm>([]);
  readonly form = this.fb.nonNullable.group({
    audience: ['guest' as GuidebookAudience],
    addTarget: ['custom'],
    text: [''],
    label: ['', Validators.required],
    value: [''],
    placement: [OWN_SECTION],
    fields: this.fields,
    rows: this.rows,
  });

  boundSave = () => this.save();
  boundAddRow = () => this.addRow();

  constructor() {
    // The confirmation runs in its own modal, so this OnPush editor has to drop the photo itself.
    this.photoCaptionService.deleteImage$.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.removeDeletedPhoto());
  }

  async ngOnInit() {
    try {
      this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;
      this.params = await this.storage.retrieve('dialog-params');
      this.addressId = this.params?.addressId ?? null;
      this.audience = this.params?.audience ?? 'guest';
      if (!this.addressId || !this.params?.target) {
        this.errorMessage = 'Missing section reference. Please reopen the guidebook.';
        this.isDataLoaded = true;
        this.cdr.markForCheck();
        return;
      }
      const address = await this.addressGuidebookService.getAddressWithSettings(this.addressId);
      this.model = buildGuidebookModel(address, this.isRental);
      this.addressPhotoNotes = Array.isArray(address['photo_notes']) ? address['photo_notes'] as PhotoNoteRoomModel[] : [];
      this.setupTarget();
    } catch {
      this.errorMessage = 'Failed to load this section.';
    }
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  removeRow(index: number) {
    const row = this.rows.at(index);
    if (row.controls.key.value) {
      row.controls.deleted.setValue(true);
    } else {
      this.rows.removeAt(index);
    }
    this.cdr.markForCheck();
  }

  addRow() {
    this.rows.push(this.buildRow({ key: '', label: '', value: '' }));
    this.cdr.markForCheck();
  }

  async addPhoto() {
    if (!this.photoType) { return; }
    try {
      this.photoUrls.push(await this.photoCaptionService.getImg(this.photoType));
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || '';
    }
    this.cdr.markForCheck();
  }

  async confirmDeletePhoto(photoId: number) {
    if (!this.photoUrls.some(photo => photo.id === photoId)) { return; }
    this.pendingDeletePhotoId = photoId;
    try {
      await this.photoCaptionService.confirmDelete(photoId, this.photoUrls);
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || '';
      this.cdr.markForCheck();
    }
  }

  private removeDeletedPhoto() {
    const photoId = this.pendingDeletePhotoId;
    this.pendingDeletePhotoId = null;
    if (photoId === null) { return; }
    this.photoUrls = this.photoUrls.filter(photo => photo.id !== photoId);
    this.cdr.markForCheck();
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('guidebook');
    }
  }

  async save() {
    this.submitted = true;
    const model = this.model;
    if (!this.target || !model || !this.addressId || this.demoBlock.block()) { return; }
    const payload = this.buildPayload(model);
    if (!payload) {
      this.cdr.markForCheck();
      return;
    }
    this.isSaving = true;
    this.cdr.markForCheck();
    try {
      if (this.photoType) {
        await this.uploadPhotos(this.addressId, this.photoType);
      }
      if (payload.upserts.length) {
        await this.addressGuidebookService.upsertGuidebook(this.addressId, payload.upserts);
      }
      if (payload.deletes.length) {
        await this.addressGuidebookService.deleteGuidebookSettings(this.addressId, payload.deletes);
      }
      if (Object.keys(payload.addressPatch).length) {
        await this.addresses.updateAddress(payload.addressPatch, this.addressId);
      }
      this.util.showSuccess('Saved', 2000);
      this.goBack();
    } catch {
      this.util.showError('Failed to save. Please try again.', 3000);
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }

  private setupTarget() {
    const params = this.params!;
    switch (params.target.type) {
      case 'text':
        this.resolveTarget({ type: 'text', key: params.target.key });
        break;
      case 'category':
        this.resolveTarget({ type: 'category', categoryId: params.target.categoryId });
        break;
      case 'custom':
        this.resolveTarget({ type: 'custom', key: params.target.key });
        break;
      case 'new':
        this.setupAddFlow();
        break;
    }
  }

  // One "Add Section" button covers both guidebooks, so the audience radio drives the flow.
  // For the guest side the picker can also fill an empty fixed slot or an empty section.
  private setupAddFlow() {
    this.isAddFlow = true;
    this.pageTitle = 'Add Section';
    this.addOptions = this.buildAddOptions();
    this.form.controls.audience.setValue(this.audience, { emitEvent: false });
    this.form.controls.audience.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.audience = value;
      this.form.controls.addTarget.setValue('custom', { emitEvent: false });
      this.applyAddAudience();
      this.cdr.markForCheck();
    });
    this.form.controls.addTarget.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.resolveTarget(this.parseAddTarget(value));
      this.cdr.markForCheck();
    });
    this.applyAddAudience();
  }

  // Internal notes always start as their own custom entry; only guest sections get the picker.
  private applyAddAudience() {
    const { label, value } = this.form.getRawValue();
    this.showAddPicker = this.audience === 'guest' && this.addOptions.length > 1;
    this.resolveTarget({ type: 'custom', key: null });
    // Flipping the radio shouldn't throw away what's already been typed.
    this.form.patchValue({ label, value }, { emitEvent: false });
  }

  private buildAddOptions(): SelectModel[] {
    const entries = this.model!.entries;
    const options: SelectModel[] = [{ value: 'custom', viewValue: 'Custom section' }];
    for (const field of TOP_LEVEL_FIELDS) {
      const entry = entries.find(candidate => candidate.key === field.key);
      if (entry && entry.value.trim() === '') {
        options.push({ value: `text:${field.key}`, viewValue: field.label });
      }
    }
    for (const category of GUIDEBOOK_V2_CATEGORIES) {
      if (category.id === 'contacts' || category.fixed || this.model!.categoryVisibility[category.id] === false) { continue; }
      const hasContent = entries.some(entry =>
        entry.categoryId === category.id && entry.audience === 'guest' && entry.value.trim() !== ''
      );
      if (!hasContent) {
        options.push({ value: `category:${category.id}`, viewValue: category.label });
      }
    }
    return options;
  }

  private parseAddTarget(value: string): ResolvedTarget {
    if (value.startsWith('text:')) {
      return { type: 'text', key: value.slice('text:'.length) };
    }
    if (value.startsWith('category:')) {
      return { type: 'category', categoryId: value.slice('category:'.length) as GuidebookV2CategoryId };
    }
    return { type: 'custom', key: null };
  }

  private resolveTarget(target: ResolvedTarget) {
    const entries = this.model!.entries;
    this.target = target;
    this.submitted = false;
    const isNew = this.params?.target.type === 'new';
    switch (target.type) {
      case 'text': {
        const entry = entries.find(candidate => candidate.key === target.key);
        this.textLabel = entry?.label ?? 'Content';
        this.form.controls.text.setValue(entry?.value ?? '', { emitEvent: false });
        if (!isNew) { this.pageTitle = `Edit ${this.textLabel}`; }
        this.saveLabel = isNew ? 'Publish to Website' : 'Save';
        break;
      }
      case 'category': {
        this.setupCategory(target.categoryId);
        if (!isNew) { this.pageTitle = `Edit ${this.categoryLabel}`; }
        this.saveLabel = isNew ? 'Publish to Website' : 'Save';
        break;
      }
      case 'custom': {
        const entry = target.key ? entries.find(candidate => candidate.key === target.key) : null;
        this.form.patchValue({
          label: entry?.label ?? '',
          value: entry?.value ?? '',
          placement: entry?.categoryId ?? OWN_SECTION,
        }, { emitEvent: false });
        if (!isNew) { this.pageTitle = this.audience === 'internal' ? 'Edit Note' : 'Edit Section'; }
        this.saveLabel = isNew
          ? (this.audience === 'guest' ? 'Publish to Website' : 'Save Note')
          : 'Save';
        break;
      }
    }
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle(this.pageTitle);
    }
  }

  // Codes live on the stay, so the editor hands over to the reservation to change one.
  openStay(reservation: ReservationSummary) {
    if (this.demoBlock.block()) { return; }
    const params = { guestReservation: reservation, isInquiry: false, addressId: this.addressId };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Reservation');
      void this.rightSidePanelService.navigateTo('reservation', params, ReservationPage);
    } else {
      this.navCtrl.navigateForward('reservation', params);
    }
  }

  async openGenerateCode(remoteAccessDeviceId: number) {
    if (this.demoBlock.block()) { return; }
    const params = { preselectedDeviceId: remoteAccessDeviceId };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.setDialogPageTitle('Generate Access Code');
      await this.rightSidePanelService.navigateTo('smartlock-test-code', params, SmartlockTestCodeComponent);
    } else {
      await this.storage.save('dialog-params', params);
      this.navCtrl.navigateForward('smartlock-test-code', params);
    }
  }

  private setupCategory(categoryId: GuidebookV2CategoryId) {
    const category = GUIDEBOOK_V2_CATEGORIES.find(candidate => candidate.id === categoryId);
    const entries = this.model!.entries;
    this.categoryLabel = category?.label ?? 'Section';
    this.categoryFields = category?.fields ?? [];
    this.isFixedCategory = !!category?.fixed;
    this.isAccessCategory = categoryId === 'access' && this.audience === 'guest';
    for (const key of Object.keys(this.fields.controls)) {
      this.fields.removeControl(key, { emitEvent: false });
    }
    for (const field of this.categoryFields) {
      const entry = entries.find(candidate => candidate.key === field.key);
      // Selects edit the stored value; text fields edit the text itself.
      const initial = field.inputType === 'select' ? (entry?.rawValue ?? null) as FieldValue : entry?.value ?? '';
      this.fields.addControl(field.key, this.fb.control<FieldValue>(initial), { emitEvent: false });
    }
    this.fieldControls = this.fields.controls;
    this.watchPaidParking();
    this.photoType = category?.photoKind ? guidebookPhotoType(category.photoKind, this.isRental) : null;
    this.photoUrls = this.photoType ? this.photoCaptionService.mountPhotoUrls(this.addressPhotoNotes, this.photoType) : [];
    this.rows.clear({ emitEvent: false });
    for (const entry of entries) {
      if (entry.kind !== 'custom' || entry.categoryId !== categoryId || entry.audience !== 'guest') { continue; }
      this.rows.push(this.buildRow({ key: entry.key, label: entry.label, value: entry.value }), { emitEvent: false });
    }
  }

  // Switching free / paid clears the spot and payment details the way Edit Property does,
  // since the spot list and the payment fields depend on it. Re-picking the same type
  // still emits, so only a real change clears them.
  private watchPaidParking() {
    this.paidParkingSubscription?.unsubscribe();
    this.paidParkingSubscription = null;
    const paid = this.fieldControls[PAID_PARKING_FIELD_KEY];
    if (paid) {
      const dependents = paidParkingDependentKeys(this.categoryFields);
      this.paidParkingSubscription = paid.valueChanges.pipe(
        startWith(paid.value),
        distinctUntilChanged(),
        skip(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        for (const key of dependents) {
          this.fieldControls[key].setValue(null, { emitEvent: false });
        }
        this.applyParkingRules();
        this.cdr.markForCheck();
      });
    }
    this.applyParkingRules();
  }

  private applyParkingRules() {
    const isPaid = this.fieldControls[PAID_PARKING_FIELD_KEY]?.value === true;
    this.visibleCategoryFields = this.categoryFields.filter(field => !field.onlyWhenPaid || isPaid);
    const options: Record<string, ParkingOption[]> = {};
    for (const field of this.categoryFields) {
      if (field.options) {
        options[field.key] = isPaid && field.optionsWhenPaid ? field.optionsWhenPaid : field.options;
      }
    }
    this.fieldOptions = options;
  }

  private async uploadPhotos(addressId: number, type: PhotoNoteType) {
    await this.photoCaptionService.uploadPhotosGeneric(
      this.photoUrls,
      type,
      (s3PhotoUrlKey: string) => this.photoNotes.saveAddressPhotoNote(type, s3PhotoUrlKey, addressId),
      (photoNoteId: number, caption: string) => this.photoNotes.photoCaption(photoNoteId, caption),
      'room'
    );
  }

  private buildRow(row: { key: string; label: string; value: string }): RowForm {
    return this.fb.nonNullable.group({
      key: [row.key],
      label: [row.label],
      value: [row.value],
      deleted: [false],
    });
  }

  private buildPayload(model: GuidebookModel): {
    upserts: UpsertGuidebookEntry[];
    deletes: string[];
    addressPatch: Record<string, unknown>;
  } | null {
    const target = this.target!;
    const entries = model.entries;
    const visibility = model.categoryVisibility;
    const upserts: UpsertGuidebookEntry[] = [];
    const deletes: string[] = [];
    const addressPatch: Record<string, unknown> = {};
    const existingKeys = new Set(entries.map(entry => entry.key));
    const isNew = this.params?.target.type === 'new';
    const raw = this.form.getRawValue();

    switch (target.type) {
      case 'text': {
        const entry = entries.find(candidate => candidate.key === target.key);
        if (!entry) { return null; }
        // Filling an empty slot from "Add Section" always publishes it; editing keeps its side.
        const updated = withAudience({ ...entry, value: raw.text }, isNew ? 'guest' : entry.audience);
        const row = entryUpsert(updated, visibility);
        if (row) { upserts.push(row); }
        break;
      }
      case 'category': {
        Object.assign(addressPatch, buildCategoryAddressPatch(this.categoryFields, entries, raw.fields));
        for (const field of this.categoryFields) {
          const entry = entries.find(candidate => candidate.key === field.key);
          if (!entry || (entry.kind === 'address' && entry.addressField)) { continue; }
          const fieldValue = raw.fields[field.key] ?? null;
          const row = entryUpsert({ ...entry, value: String(fieldValue ?? '') }, visibility);
          if (row) { upserts.push(row); }
        }
        for (const rowValue of raw.rows) {
          const label = (rowValue.label || '').trim();
          const value = rowValue.value || '';
          if (rowValue.deleted) {
            if (rowValue.key) { deletes.push(rowValue.key); }
            continue;
          }
          if (rowValue.key) {
            const entry = entries.find(candidate => candidate.key === rowValue.key);
            if (!entry) { continue; }
            const row = entryUpsert(withText(entry, label || entry.label, value), visibility);
            if (row) { upserts.push(row); }
            continue;
          }
          if (!label && !value) { continue; }
          if (!label) {
            this.util.showError('Please give each new row a title.', 3000);
            return null;
          }
          const created = createCustomEntry(
            { label, value, audience: 'guest', categoryId: target.categoryId },
            existingKeys
          );
          existingKeys.add(created.key);
          const row = entryUpsert(created, visibility);
          if (row) { upserts.push(row); }
        }
        break;
      }
      case 'custom': {
        const label = (raw.label || '').trim();
        if (!label) { return null; }
        const placement = raw.placement === OWN_SECTION ? null : raw.placement as GuidebookV2CategoryId;
        if (target.key) {
          const entry = entries.find(candidate => candidate.key === target.key);
          if (!entry) { return null; }
          let updated = withText(entry, label, raw.value);
          if (this.audience === 'guest') {
            updated = { ...updated, categoryId: placement };
          }
          const row = entryUpsert(updated, visibility);
          if (row) { upserts.push(row); }
        } else {
          const created = createCustomEntry({
            label,
            value: raw.value,
            audience: this.audience,
            categoryId: this.audience === 'guest' ? placement : null,
          }, existingKeys);
          const row = entryUpsert(created, visibility);
          if (row) { upserts.push(row); }
        }
        break;
      }
    }
    return { upserts, deletes, addressPatch };
  }
}
